/**
 * The one command a learner pastes to start (owner, 2026-10-28: "give this
 * to your agent"). It starts Claude Code with the EngineerOS server and the
 * cost report set for that run only, so nothing has to be edited first:
 * `--mcp-config` carries the server and the key, the environment carries the
 * report (the same settings as `reportSettings` in lib/agent-keys.ts), and
 * `--allowedTools` lets the agent use this server's tools without asking for
 * each one. Those tools reach only the lent tab's workspace, and
 * `--disallowedTools` takes away the ones that reach this computer: a level
 * is done in the tab, and an agent whose server has not connected (a revoked
 * key, a dropped network) otherwise goes looking through the learner's files
 * for it. Fewer tools is also a cheaper call, and every run the same.
 *
 * A prompt, when there is one, comes first: the tool options and
 * `--mcp-config` take lists and would swallow anything after them.
 * PowerShell 5.1 strips the quotes inside an argument it hands a program, so
 * there the server goes through a file.
 */
import { AI_FALLBACK_ORIGIN, AI_REPORT_PATH } from './agent-keys';

export type Shell = 'posix' | 'powershell';

/** The server's name in Claude Code: its tools are `mcp__engineeros__*`. */
const SERVER = 'engineeros';

/** Claude Code's tools that reach the learner's computer, the web or other agents. */
const LOCAL_TOOLS = ['Bash', 'PowerShell', 'Read', 'Write', 'Edit', 'Glob', 'Grep', 'NotebookEdit', 'WebFetch', 'WebSearch', 'Task', 'Agent'];

/**
 * With no `prompt`, Claude Code opens connected and waits for the learner's
 * first message: the learner holds the task and tells their agent what they
 * need (owner, 2026-10-30). A lab passes a prompt to start a run unattended.
 */
export function agentCommand(origin: string, key: string, shell: Shell, prompt?: string): string {
  const base = /^https?:\/\/[^\s"']+$/.test(origin.trim()) ? origin.trim().replace(/\/+$/, '') : AI_FALLBACK_ORIGIN;
  const mcp = JSON.stringify({ mcpServers: { [SERVER]: { type: 'http', url: `${base}/api/mcp`, headers: { Authorization: `Bearer ${key}` } } } });
  const env: [string, string][] = [
    ['CLAUDE_CODE_ENABLE_TELEMETRY', '1'],
    ['OTEL_LOGS_EXPORTER', 'otlp'],
    ['OTEL_EXPORTER_OTLP_PROTOCOL', 'http/json'],
    ['OTEL_EXPORTER_OTLP_ENDPOINT', `${base}${AI_REPORT_PATH}`],
    ['OTEL_EXPORTER_OTLP_HEADERS', `Authorization=Bearer ${key}`],
    ['OTEL_LOGS_EXPORT_INTERVAL', '5000'],
  ];
  const quoted = (value: string) => `'${value.replace(/'/g, shell === 'posix' ? `'\\''` : `''`)}'`;
  const claude = prompt ? `claude ${quoted(prompt)}` : 'claude';
  const tools = `--disallowedTools ${LOCAL_TOOLS.join(' ')} --allowedTools ${quoted(`mcp__${SERVER}`)}`;
  if (shell === 'posix') {
    return `${env.map(([k, v]) => `${k}=${quoted(v)}`).join(' ')} ${claude} ${tools} --mcp-config ${quoted(mcp)}`;
  }
  const file = `"$env:TEMP\\${SERVER}-mcp.json"`;
  return [
    ...env.map(([k, v]) => `$env:${k}=${quoted(v)}`),
    `Set-Content -Encoding ascii ${file} ${quoted(mcp)}`,
    `${claude} ${tools} --mcp-config ${file}`,
  ].join('; ');
}

/** The shell a visitor most likely has: PowerShell on Windows, otherwise a POSIX shell. */
export function likelyShell(platform: string): Shell {
  return /^win/i.test(platform) ? 'powershell' : 'posix';
}
